/**
 * Employee Completion Form — mobile-first form for technicians to submit a service completion.
 * Mandatory: before+after photos, customer signature (canvas), GPS, chemicals used, target pest.
 * POSTs to /api/panel/employee/work-orders/{booking_id}/submit-completion
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Camera, MapPin, PenTool, X, Check, Loader2, AlertCircle,
  ChevronLeft, Package,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/panel`;
const UPLOAD = `${process.env.REACT_APP_BACKEND_URL}/api/panel/employee/uploads/work-order-photo`;

/* ---------- Predefined catalogs (technician-friendly, pure pick-lists) ---------- */

// Chemicals — name + concentration + default dose. Default unit = ml.
const CHEMICALS = [
  { value: 'Termidor SC 9.1%',          label: 'Termidor SC 9.1% (Fipronil) — termites' },
  { value: 'Premise 30.5% SC',          label: 'Premise 30.5% SC (Imidacloprid) — termites / ants' },
  { value: 'Lambda-cyhalothrin 2.5% EC',label: 'Lambda-cyhalothrin 2.5% EC — general crawling' },
  { value: 'Cypermethrin 25% EC',       label: 'Cypermethrin 25% EC — cockroach / general' },
  { value: 'Deltamethrin 2.5% SC',      label: 'Deltamethrin 2.5% SC — mosquito fogging' },
  { value: 'Imidacloprid 17.8% SL',     label: 'Imidacloprid 17.8% SL — cockroach gel base' },
  { value: 'Fipronil 0.05% Gel',        label: 'Fipronil 0.05% Gel — cockroach baiting' },
  { value: 'Bromadiolone 0.005% Wax',   label: 'Bromadiolone 0.005% Wax Block — rodents' },
  { value: 'Zinc Phosphide 80%',        label: 'Zinc Phosphide 80% — rodents (outdoor)' },
  { value: 'Aluminium Phosphide Tablets',label:'Aluminium Phosphide Tablets — rodent burrows' },
  { value: 'Pyrethrum Extract 2%',      label: 'Pyrethrum Extract 2% — flying insects (food-safe)' },
  { value: 'Bedlam Plus',               label: 'Bedlam Plus — bedbug knock-down spray' },
  { value: 'Sodium Hypochlorite 5%',    label: 'Sodium Hypochlorite 5% — disinfection' },
  { value: 'Custom (write in notes)',   label: 'Other — write actual product in notes' },
];

// Quantity in ml (chemical solution dispensed)
const QUANTITY_OPTIONS_ML = [
  '100 ml', '250 ml', '500 ml', '750 ml',
  '1 L', '1.5 L', '2 L', '3 L', '5 L', '10 L', '15 L', '20 L',
];

// Property types (Indian residential + commercial)
const PROPERTY_TYPES = [
  '1 RK', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK+',
  'Studio Apartment', 'Independent House / Villa',
  'Office < 1000 sqft', 'Office 1000-3000 sqft', 'Office 3000+ sqft',
  'Shop / Retail', 'Warehouse / Godown', 'Restaurant / Kitchen',
  'Hospital / Clinic', 'School / Institute', 'Factory / Industrial',
  'Hotel / Hostel', 'Society / Common Area',
];

// Packet/Kit options — each has a description so techs know what's inside.
const PACKET_KITS = [
  { value: '',                              label: '— none —',                           desc: '' },
  { value: 'Termite Pre-Construction Kit',  label: 'Termite Pre-Construction Kit',       desc: 'Termidor 1L + soil treatment tools + drill bits' },
  { value: 'Termite Post-Construction Kit', label: 'Termite Post-Construction Kit',      desc: '500 ml Termidor SC + injection rods + sealant' },
  { value: 'Cockroach Gel Pack',            label: 'Cockroach Gel Pack',                 desc: '4 x 5g Fipronil gel syringes + applicator nozzle' },
  { value: 'Cockroach Spray Kit',           label: 'Cockroach Spray Kit',                desc: 'Cypermethrin 250ml + Pyrethrum 100ml + sprayer' },
  { value: 'Mosquito Fogging Pack',         label: 'Mosquito Fogging Pack',              desc: 'Deltamethrin 500ml + diesel mix + fogger fuel' },
  { value: 'Mosquito Larvicide Pack',       label: 'Mosquito Larvicide Pack',            desc: 'Temephos granules 250g + dispenser' },
  { value: 'Bedbug Kit',                    label: 'Bedbug Kit',                         desc: 'Bedlam Plus 500ml + Imidacloprid 250ml + steam wand' },
  { value: 'Rodent Bait Station Pack',      label: 'Rodent Bait Station Pack',           desc: '6 bait stations + Bromadiolone wax blocks 200g' },
  { value: 'Rodent Trap Pack',              label: 'Rodent Trap Pack',                   desc: '4 snap traps + 4 glue boards + bait' },
  { value: 'General Pest Spray Pack',       label: 'General Pest Spray Pack',            desc: 'Lambda-cyhalothrin 500ml + Cypermethrin 250ml + sprayer' },
  { value: 'Wood Borer Kit',                label: 'Wood Borer Kit',                     desc: 'Boron-based 500ml + injection needle + brush' },
  { value: 'Disinfection / Sanitization Kit',label:'Disinfection / Sanitization Kit',    desc: 'Sodium hypochlorite 1L + atomizer + PPE' },
  { value: 'AMC Quarterly Visit Pack',      label: 'AMC Quarterly Visit Pack',           desc: 'Multi-pest combo (gel + spray + dusting powder)' },
];

const DURATION_OPTIONS = [
  '15', '30', '45', '60', '75', '90', '120', '150', '180', '240',
];

export default function EmployeeCompletionForm({ booking, token, onCancel, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [pests, setPests] = useState([]);
  const [form, setForm] = useState({
    target_pest: '',
    chemicals_used: '',
    chemical_qty_value: '',
    chemical_qty_unit: 'ml',
    quantity_used: '',
    service_duration_min: '',
    property_type: '',
    packet_kit: '',
    customer_feedback: '',
    service_notes: '',
    customer_signature: '',
    gps_lat: 0, gps_lng: 0,
    before_photos: [],
    after_photos: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${API}/admin/pest-catalog`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setPests(d.items || [])).catch(() => {});
  }, [token]);

  // Whenever chemical qty or unit changes, build the canonical `quantity_used` string.
  useEffect(() => {
    if (form.chemical_qty_value) {
      const v = `${form.chemical_qty_value} ${form.chemical_qty_unit}`.trim();
      setForm((f) => (f.quantity_used === v ? f : { ...f, quantity_used: v }));
    }
  }, [form.chemical_qty_value, form.chemical_qty_unit]);

  const grabGPS = () => {
    if (!navigator.geolocation) { setErr('GPS not available on this device'); return; }
    setErr('');
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude })),
      (e) => setErr(`GPS error: ${e.message}`),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const uploadPhoto = async (file, which) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(UPLOAD, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) throw new Error(`Upload failed (${r.status})`);
      const data = await r.json();
      const url = data.url || data.path || `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${data.filename || ''}`;
      setForm(f => ({ ...f, [which]: [...f[which], url] }));
    } catch (e) { setErr(e.message); }
  };

  const removePhoto = (which, i) => {
    setForm(f => ({ ...f, [which]: f[which].filter((_, idx) => idx !== i) }));
  };

  const submit = async () => {
    if (form.before_photos.length < 1) return setErr('At least 1 BEFORE photo required');
    if (form.after_photos.length < 1) return setErr('At least 1 AFTER photo required');
    if (!form.customer_signature || form.customer_signature.length < 50) return setErr('Customer signature required');
    if (form.gps_lat === 0 && form.gps_lng === 0) return setErr('Tap "Capture GPS" to record location');
    if (!form.target_pest?.trim()) return setErr('Target pest is required');
    if (!form.chemicals_used?.trim()) return setErr('Chemicals used is required');

    setBusy(true); setErr('');
    try {
      const packetInfo = PACKET_KITS.find((p) => p.value === form.packet_kit);
      const packetText = packetInfo?.value
        ? `\n\n[Packet/Kit used] ${packetInfo.value} — ${packetInfo.desc}`
        : '';
      const payload = {
        ...form,
        // Bundle packet info into service_notes since backend has no packet column
        service_notes: `${form.service_notes || ''}${packetText}`.trim(),
      };
      // Strip helper-only fields not expected by backend
      delete payload.chemical_qty_value;
      delete payload.chemical_qty_unit;
      delete payload.packet_kit;
      ['service_duration_min'].forEach(k => { if (payload[k]) payload[k] = Number(payload[k]); else delete payload[k]; });
      const r = await fetch(`${API}/employee/work-orders/${booking.id}/submit-completion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Submit failed (${r.status})`); }
      onSubmitted();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Fallback pest list if backend pest catalog is empty for any reason
  const FALLBACK_PESTS = [
    'Termite', 'Cockroach', 'Bedbug', 'Mosquito', 'Rodent / Rat',
    'Ant', 'Lizard / Gecko', 'Spider', 'Wood Borer', 'Honey Bee / Wasp',
    'Snake', 'Fly', 'Silverfish', 'General Pest Control',
  ];
  const pestOptions = pests.filter((p) => p.active).length
    ? pests.filter((p) => p.active).map((p) => ({ key: p.slug, label: p.name }))
    : FALLBACK_PESTS.map((p) => ({ key: p, label: p }));

  const packetInfo = PACKET_KITS.find((p) => p.value === form.packet_kit);

  const stepNav = (
    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-200">
      {step > 1 ? <Button type="button" variant="outline" onClick={() => setStep(step - 1)} data-testid="comp-back"><ChevronLeft size={14} className="mr-1" /> Back</Button> : <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>}
      {step < 4 ? <Button type="button" onClick={() => setStep(step + 1)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="comp-next">Next</Button>
                : <Button type="button" onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="comp-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" /> Submit for Approval</>}</Button>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" data-testid="comp-form-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-2xl overflow-hidden flex flex-col max-h-[96vh]">
        <div className="px-5 py-3.5 bg-emerald-700 text-white">
          <div className="text-xs opacity-90">The Indian Pest Control · Job Completion</div>
          <div className="font-bold text-sm">{booking.booking_code || booking.id?.slice(0, 8)} · {booking.customer_name}</div>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4].map(n => <div key={n} className={`flex-1 h-1.5 rounded-full ${step >= n ? 'bg-white' : 'bg-white/30'}`} />)}
          </div>
        </div>

        <div className="overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">1 · Service Details</h3>

              <div>
                <Label>Target Pest *</Label>
                <select value={form.target_pest} onChange={(e) => setForm({ ...form, target_pest: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white" data-testid="comp-pest">
                  <option value="">— select pest —</option>
                  {pestOptions.map((p) => <option key={p.key} value={p.label}>{p.label}</option>)}
                </select>
              </div>

              <div>
                <Label>Chemicals Used *</Label>
                <select value={form.chemicals_used} onChange={(e) => setForm({ ...form, chemicals_used: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white" data-testid="comp-chem">
                  <option value="">— select chemical —</option>
                  {CHEMICALS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Quantity Used</Label>
                  <select
                    value={form.chemical_qty_value ? `${form.chemical_qty_value} ${form.chemical_qty_unit}` : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setForm((f) => ({ ...f, chemical_qty_value: '', quantity_used: '' }));
                        return;
                      }
                      const [val, unit] = v.split(' ');
                      setForm((f) => ({ ...f, chemical_qty_value: val, chemical_qty_unit: unit || 'ml' }));
                    }}
                    className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white"
                    data-testid="comp-qty"
                  >
                    <option value="">— select amount —</option>
                    {QUANTITY_OPTIONS_ML.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                  <div className="text-[10px] text-slate-500 mt-0.5">Default unit: ml (litres for larger jobs)</div>
                </div>
                <div>
                  <Label>Duration</Label>
                  <select value={form.service_duration_min} onChange={(e) => setForm({ ...form, service_duration_min: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white" data-testid="comp-dur">
                    <option value="">— select —</option>
                    {DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label>Property Type</Label>
                <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white" data-testid="comp-prop">
                  <option value="">— select property —</option>
                  {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Package size={12} /> Packet / Kit Used</Label>
                <select value={form.packet_kit} onChange={(e) => setForm({ ...form, packet_kit: e.target.value })} className="h-11 w-full rounded-md border border-slate-200 px-3 text-base bg-white" data-testid="comp-packet">
                  {PACKET_KITS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {packetInfo?.desc && (
                  <div className="text-[11px] text-slate-600 mt-1 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="comp-packet-desc">
                    <b className="text-emerald-800">Contents:</b> {packetInfo.desc}
                  </div>
                )}
              </div>

              <div>
                <Label>Extra notes (optional)</Label>
                <textarea value={form.service_notes} onChange={(e) => setForm({ ...form, service_notes: e.target.value })} rows={3} className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="Anything special you observed or recommend follow-up for…" data-testid="comp-notes" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">2 · Before & After Photos</h3>
              <PhotoZone label="BEFORE photos *" photos={form.before_photos} onAdd={(f) => uploadPhoto(f, 'before_photos')} onRemove={(i) => removePhoto('before_photos', i)} testidPrefix="before" />
              <PhotoZone label="AFTER photos *" photos={form.after_photos} onAdd={(f) => uploadPhoto(f, 'after_photos')} onRemove={(i) => removePhoto('after_photos', i)} testidPrefix="after" />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">3 · Customer Signature & Feedback</h3>
              <SignaturePad value={form.customer_signature} onChange={(v) => setForm({ ...form, customer_signature: v })} />
              <div><Label>Customer Feedback</Label><textarea value={form.customer_feedback} onChange={(e) => setForm({ ...form, customer_feedback: e.target.value })} rows={3} className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm" placeholder="What did the customer say?" data-testid="comp-fb" /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900">4 · Verify Location & Submit</h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <Label>GPS Location *</Label>
                <Button type="button" onClick={grabGPS} variant="outline" className="w-full mt-1 bg-white" data-testid="comp-gps"><MapPin size={14} className="mr-1.5" /> {form.gps_lat ? `${form.gps_lat.toFixed(5)}, ${form.gps_lng.toFixed(5)}` : 'Capture current location'}</Button>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1.5">
                <div className="font-bold text-slate-900 text-sm mb-1">Review</div>
                <div>🐛 Pest: <b>{form.target_pest || '⚠️ missing'}</b></div>
                <div>💊 Chemical: <b>{form.chemicals_used || '⚠️ missing'}</b></div>
                <div>⚗️ Quantity: <b>{form.quantity_used || '—'}</b></div>
                <div>⏱️ Duration: <b>{form.service_duration_min ? `${form.service_duration_min} min` : '—'}</b></div>
                <div>🏠 Property: <b>{form.property_type || '—'}</b></div>
                {form.packet_kit && <div>📦 Packet: <b>{form.packet_kit}</b></div>}
                <div>📷 Before: <b>{form.before_photos.length}</b> · After: <b>{form.after_photos.length}</b></div>
                <div>✍️ Signature: <b>{form.customer_signature?.length > 50 ? '✓ captured' : '⚠️ missing'}</b></div>
                <div>📍 GPS: <b>{form.gps_lat ? '✓ captured' : '⚠️ pending'}</b></div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs">
                <b className="text-blue-900">What happens next:</b> Once you submit, this goes to Admin Approval. After admin approves: GST invoice + warranty certificate are auto-generated and emailed to the customer.
              </div>
            </div>
          )}

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-2 mt-3 flex items-start gap-2"><AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {err}</div>}
        </div>

        <div className="px-5 pb-5">
          {stepNav}
        </div>
      </div>
    </div>
  );
}

function PhotoZone({ label, photos, onAdd, onRemove, testidPrefix }) {
  const inputRef = useRef(null);
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden" data-testid={`${testidPrefix}-photo-${i}`}>
            <img src={p} alt={`${testidPrefix}-${i}`} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onRemove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={12} /></button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:bg-slate-50 hover:border-emerald-400" data-testid={`${testidPrefix}-add`}>
          <Camera size={20} /><span className="text-[10px] mt-1">Add</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { if (e.target.files?.[0]) onAdd(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#0A2540';
  }, []);

  const point = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); const p = point(e); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(p.x, p.y); setDrawing(true); };
  const draw = (e) => { if (!drawing) return; e.preventDefault(); const p = point(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); };
  const end = () => { setDrawing(false); if (hasInk) onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasInk(false); onChange(''); };

  return (
    <div>
      <Label className="text-sm flex items-center justify-between">
        <span className="inline-flex items-center gap-1"><PenTool size={12} /> Customer Signature *</span>
        <button type="button" onClick={clear} className="text-xs text-rose-600 hover:underline" data-testid="sig-clear">Clear</button>
      </Label>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white mt-1">
        <canvas
          ref={canvasRef}
          width={600} height={180}
          className="w-full h-44 touch-none rounded-lg"
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
          data-testid="sig-canvas"
        />
      </div>
      <div className="text-[10px] text-slate-500 mt-1">Ask the customer to sign with their finger or stylus.</div>
    </div>
  );
}
